import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  GridLegacy as Grid,
  IconButton,
  Chip,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import './FeeCategories.css';

interface FeeCategory {
  _id?: string;
  name: string;
  description?: string;
  ruleType: string;
  conditions: {
    spotTypes: string[];
    vehicleTypes: string[];
    floors: number[];
    zones: string[];
  };
  pricing: {
    baseRate: number;
    rateUnit: string;
    minimumCharge: number;
    maximumCharge?: number;
    freeMinutes: number;
    billingInterval: number;
  };
  settings: {
    entryConfirmation: boolean;
    exitConfirmation: boolean;
    allowOverstay: boolean;
    duplicateEntry: boolean;
    duplicateExit: boolean;
    modifyExitWeight: boolean;
    templateRecognition: boolean;
    supportTechnicalExpiry: boolean;
  };
  isActive: boolean;
  priority: number;
}

const FeeCategories: React.FC = () => {
  const [categories, setCategories] = useState<FeeCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FeeCategory | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [formData, setFormData] = useState<FeeCategory>({
    name: '',
    description: '',
    ruleType: 'hourly',
    conditions: {
      spotTypes: [],
      vehicleTypes: [],
      floors: [],
      zones: []
    },
    pricing: {
      baseRate: 0,
      rateUnit: 'per_hour',
      minimumCharge: 0,
      maximumCharge: undefined,
      freeMinutes: 0,
      billingInterval: 60
    },
    settings: {
      entryConfirmation: false,
      exitConfirmation: false,
      allowOverstay: false,
      duplicateEntry: false,
      duplicateExit: false,
      modifyExitWeight: false,
      templateRecognition: false,
      supportTechnicalExpiry: false
    },
    isActive: true,
    priority: 1
  });

  // 選項數據
  const ruleTypeOptions = [
    { value: 'hourly', label: '時租' },
    { value: 'daily', label: '日租' },
    { value: 'monthly', label: '月租' },
    { value: 'flat_rate', label: '固定費率' },
    { value: 'progressive', label: '累進費率' },
    { value: 'time_based', label: '時段計費' }
  ];

  const vehicleTypeOptions = [
    { value: 'car', label: '汽車' },
    { value: 'motorcycle', label: '機車' },
    { value: 'truck', label: '貨車' },
    { value: 'van', label: '廂型車' },
    { value: 'electric', label: '電動車' }
  ];

  const spotTypeOptions = [
    { value: 'standard', label: '標準車位' },
    { value: 'compact', label: '小型車位' },
    { value: 'large', label: '大型車位' },
    { value: 'electric', label: '電動車位' },
    { value: 'disabled', label: '無障礙車位' },
    { value: 'vip', label: 'VIP車位' },
    { value: 'motorcycle', label: '機車位' }
  ];

  const rateUnitOptions = [
    { value: 'per_hour', label: '每小時' },
    { value: 'per_day', label: '每日' },
    { value: 'per_month', label: '每月' },
    { value: 'flat', label: '固定' }
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/pricing/rules');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('獲取收費類別失敗:', error);
      setSnackbar({ open: true, message: '獲取收費類別失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category?: FeeCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData(category);
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        ruleType: 'hourly',
        conditions: {
          spotTypes: [],
          vehicleTypes: [],
          floors: [],
          zones: []
        },
        pricing: {
          baseRate: 0,
          rateUnit: 'per_hour',
          minimumCharge: 0,
          maximumCharge: undefined,
          freeMinutes: 0,
          billingInterval: 60
        },
        settings: {
          entryConfirmation: false,
          exitConfirmation: false,
          allowOverstay: false,
          duplicateEntry: false,
          duplicateExit: false,
          modifyExitWeight: false,
          templateRecognition: false,
          supportTechnicalExpiry: false
        },
        isActive: true,
        priority: 1
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingCategory) {
        // 更新
        const response = await apiClient.put(`/api/pricing/rules/${editingCategory._id}`, formData);
        if (response.data.success) {
          setSnackbar({ open: true, message: '收費類別更新成功', severity: 'success' });
          fetchCategories();
          handleCloseDialog();
        }
      } else {
        // 新增
        const response = await apiClient.post('/api/pricing/rules', formData);
        if (response.data.success) {
          setSnackbar({ open: true, message: '收費類別新增成功', severity: 'success' });
          fetchCategories();
          handleCloseDialog();
        }
      }
    } catch (error: any) {
      console.error('保存收費類別失敗:', error);
      const message = error.response?.data?.message || '保存收費類別失敗';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('確定要刪除此收費類別嗎？')) {
      try {
        const response = await apiClient.delete(`/api/pricing/rules/${id}`);
        if (response.data.success) {
          setSnackbar({ open: true, message: '收費類別刪除成功', severity: 'success' });
          fetchCategories();
        }
      } catch (error: any) {
        console.error('刪除收費類別失敗:', error);
        const message = error.response?.data?.message || '刪除收費類別失敗';
        setSnackbar({ open: true, message, severity: 'error' });
      }
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof FeeCategory] as object || {}),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleArrayChange = (field: string, value: string[]) => {
    const [parent, child] = field.split('.');
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent as keyof FeeCategory] as object || {}),
        [child]: value
      }
    }));
  };

  return (
    <Box sx={{ p: 3 }} className="fee-categories">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          收費類別管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          新增收費類別
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>名稱</TableCell>
              <TableCell>類型</TableCell>
              <TableCell>基礎費率</TableCell>
              <TableCell>費率單位</TableCell>
              <TableCell>適用車輛</TableCell>
              <TableCell>狀態</TableCell>
              <TableCell>優先級</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((category) => (
              <TableRow key={category._id}>
                <TableCell>{category.name}</TableCell>
                <TableCell>
                  {ruleTypeOptions.find(opt => opt.value === category.ruleType)?.label}
                </TableCell>
                <TableCell>{category.pricing.baseRate}</TableCell>
                <TableCell>
                  {rateUnitOptions.find(opt => opt.value === category.pricing.rateUnit)?.label}
                </TableCell>
                <TableCell>
                  {category.conditions.vehicleTypes.map(type => (
                    <Chip
                      key={type}
                      label={vehicleTypeOptions.find(opt => opt.value === type)?.label || type}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </TableCell>
                <TableCell>
                  <Chip
                    label={category.isActive ? '啟用' : '停用'}
                    color={category.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{category.priority}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(category)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(category._id!)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 新增/編輯對話框 */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingCategory ? '編輯收費類別' : '新增收費類別'}
          <IconButton
            onClick={handleCloseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* 基本信息 */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>基本信息</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="車輛類型名稱"
                required
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>基礎類型</InputLabel>
                <Select
                  value={formData.ruleType}
                  label="基礎類型"
                  onChange={(e) => handleInputChange('ruleType', e.target.value)}
                >
                  {ruleTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="描述"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>

            {/* 費率設定 */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>費率設定</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="基礎費率"
                type="number"
                required
                value={formData.pricing.baseRate}
                onChange={(e) => handleInputChange('pricing.baseRate', parseFloat(e.target.value) || 0)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>識別類型</InputLabel>
                <Select
                  value={formData.pricing.rateUnit}
                  label="識別類型"
                  onChange={(e) => handleInputChange('pricing.rateUnit', e.target.value)}
                >
                  {rateUnitOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="進出間隔（分鐘）"
                type="number"
                value={formData.pricing.billingInterval}
                onChange={(e) => handleInputChange('pricing.billingInterval', parseInt(e.target.value) || 60)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="最低收費"
                type="number"
                value={formData.pricing.minimumCharge}
                onChange={(e) => handleInputChange('pricing.minimumCharge', parseFloat(e.target.value) || 0)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="免費時長（分鐘）"
                type="number"
                value={formData.pricing.freeMinutes}
                onChange={(e) => handleInputChange('pricing.freeMinutes', parseInt(e.target.value) || 0)}
              />
            </Grid>

            {/* 適用條件 */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>適用條件</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>適用車輛類型</InputLabel>
                <Select
                  multiple
                  value={formData.conditions.vehicleTypes}
                  label="適用車輛類型"
                  onChange={(e) => handleArrayChange('conditions.vehicleTypes', e.target.value as string[])}
                >
                  {vehicleTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>適用車位類型</InputLabel>
                <Select
                  multiple
                  value={formData.conditions.spotTypes}
                  label="適用車位類型"
                  onChange={(e) => handleArrayChange('conditions.spotTypes', e.target.value as string[])}
                >
                  {spotTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* 功能設定 */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>功能設定</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.settings.entryConfirmation}
                    onChange={(e) => handleInputChange('settings.entryConfirmation', e.target.checked)}
                  />
                }
                label="入場確認"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.settings.exitConfirmation}
                    onChange={(e) => handleInputChange('settings.exitConfirmation', e.target.checked)}
                  />
                }
                label="出場確認（未勾選時0元自動離開）"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.settings.allowOverstay}
                    onChange={(e) => handleInputChange('settings.allowOverstay', e.target.checked)}
                  />
                }
                label="滿車位可進"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.settings.duplicateEntry}
                    onChange={(e) => handleInputChange('settings.duplicateEntry', e.target.checked)}
                  />
                }
                label="重複入場"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.settings.duplicateExit}
                    onChange={(e) => handleInputChange('settings.duplicateExit', e.target.checked)}
                  />
                }
                label="重複出場"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.settings.modifyExitWeight}
                    onChange={(e) => handleInputChange('settings.modifyExitWeight', e.target.checked)}
                  />
                }
                label="進出修改車位"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.settings.templateRecognition}
                    onChange={(e) => handleInputChange('settings.templateRecognition', e.target.checked)}
                  />
                }
                label="車牌模糊識別"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.settings.supportTechnicalExpiry}
                    onChange={(e) => handleInputChange('settings.supportTechnicalExpiry', e.target.checked)}
                  />
                }
                label="支持技天續期"
              />
            </Grid>

            {/* 其他設定 */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>其他設定</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="優先級"
                type="number"
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  />
                }
                label="啟用此收費類別"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCategory ? '更新' : '新增'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeeCategories;